import React, { useState, useEffect } from "react";
import { fetchTiers } from '../service/apiServices'; // adjust the path
import "./PricingModel.css";
import cross from "../assets/icon/cross-circle_svgrepo.svg";
import { useAzureData } from "../context/AzureContext";
import {
  ChevronUp,
  ChevronDown,
  Headset,
  FileSearch,
  Box,
  NotepadText,
  Settings2,
  NotepadTextDashed,
  Hash,
  MoveRight,
} from "lucide-react";

interface Feature {
  category: string;
  featureName: string;
  featureValue: string;
}

interface Tier {
  tierId: number;
  tierName: string;
  price: number;
  features: Feature[];
}

interface PricingModelProps {
  handleTier: (tier: string) => void;
}

const PricingModel: React.FC<PricingModelProps> = ({ handleTier }) => {
  const { apiToken } = useAzureData();
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [selectedTier, setSelectedTier] = useState<Tier | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  useEffect(() => {
    const getTiers = async () => {
      try {
        const response = await fetchTiers(apiToken);
  
        let fetchedTiers: Tier[] = response?.tiers ?? [];
  
        // 🧠 Normalize tier names for consistency across components
        fetchedTiers = fetchedTiers.map((tier) => {
          let normalizedName = tier.tierName.trim();
  
          return {
            ...tier,
            tierName: normalizedName,
          };
        });
  
        // Custom sort logic
        const priorityMap: Record<string, number> = {
          "standard": 0,
          "standard (extended support)": 1,
          "premium": 2,
        };
  
        fetchedTiers = [...fetchedTiers].sort((a, b) => {
          const aKey = a.tierName.toLowerCase().trim();
          const bKey = b.tierName.toLowerCase().trim();
  
          const aIndex = priorityMap[aKey] ?? 99;
          const bIndex = priorityMap[bKey] ?? 99;
  
          return aIndex - bIndex;
        });
  
        setTiers(fetchedTiers);
  
        if (fetchedTiers.length > 0) {
          setSelectedTier(fetchedTiers[0]);
  
          const categories: string[] = Array.from(
            new Set(
              fetchedTiers.flatMap((tier: Tier) =>
                tier.features
                  .map((feature: Feature) => feature.category)
                  .filter((category): category is string => !!category)
              )
            )
          );
          setExpandedCategories(categories);
        }
      } catch (error) {
        console.error("Failed to fetch tiers:", error);
      }
    };
  
    if (apiToken) getTiers();
  }, [apiToken]);
  

  

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const getFeatureRow = (category: string, featureName: string) => {
    return tiers.map((t) => {
      const feature = t.features.find(
        (f) => f.category === category && f.featureName === featureName
      );
      return feature ? feature.featureValue : "";
    });
  };

  if (!selectedTier) return <div>Loading subscription plans...</div>;

  const categories = Array.from(
    new Set(tiers.flatMap((t) => t.features.map((f) => f.category ?? "")))
  ).filter((c) => c);

  return (
    <div className="pricing-layout">
      <div className="inner-container">
        {/* Tier Buttons */}
        <div className="tier-button-container">
          {tiers.map((tier) => (
            <button
              key={tier.tierId}
              className={`tier-button ${
                selectedTier.tierId === tier.tierId ? "active" : ""
              }`}
              onClick={() => setSelectedTier(tier)}
            >
              <span className="tier-title">{tier.tierName}</span>
              <span className="tier-price">
                <span className="tier-price-amount">€{tier.price}</span>
                <span className="tier-price-period">/yr</span>
                <span
                  className={`tier-radio ${
                    selectedTier.tierId === tier.tierId ? "active" : ""
                  }`}
                />
              </span>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="table-container">
          <div className="table-scroll">
            {categories.map((category) => {
              const featureNames =
                tiers[0]?.features
                  .filter((f) => f.category === category)
                  .map((f) => f.featureName) || [];

              return (
                <div key={category} className="feature-category">
                  <div
                    className="feature-category-header"
                    onClick={() => toggleCategory(category)}
                  >
                    <div className="category-cell">
                      {expandedCategories.includes(category) ? (
                        <ChevronUp size={24} color="#838FB4" />
                      ) : (
                        <ChevronDown size={24} color="#838FB4" />
                      )}

                      {category?.toLowerCase().includes("support") && (
                        <Headset size={17} color="#111111" />
                      )}
                      {category?.toLowerCase().includes("search") && (
                        <FileSearch size={17} color="#111111" />
                      )}
                      {category?.toLowerCase().includes("model") && (
                        <Box size={17} color="#111111" />
                      )}
                      {category?.toLowerCase().includes("vector") && (
                        <NotepadText size={17} color="#111111" />
                      )}
                      {category?.toLowerCase().includes("advanced") && (
                        <Settings2 size={17} color="#111111" />
                      )}
                      {category?.toLowerCase().includes("scalable") && (
                        <NotepadTextDashed size={17} color="#111111" />
                      )}
                      {category?.toLowerCase().includes("token") && (
                        <Hash size={17} color="#111111" />
                      )}
                      <span>{category}</span>
                    </div>

                    {tiers.map((tier, idx) => (
                      <div
                        key={idx}
                        className={`category-tier-cell ${
                          tier.tierId === selectedTier.tierId ? "highlight" : ""
                        }`}
                      />
                    ))}
                  </div>

                  {expandedCategories.includes(category) && (
                    <div className="feature-rows">
                      {featureNames.map((featureName, idx) => (
                        <div key={idx} className="feature-row">
                          <div className="feature-name">{featureName}</div>
                          {getFeatureRow(category, featureName).map(
                            (val, tierIdx) => (
                              <div
                                key={tierIdx}
                                className={`feature-value ${
                                  tiers[tierIdx].tierId === selectedTier.tierId
                                    ? "highlight"
                                    : ""
                                }`}
                              >
                                {val && val.trim() !== "" ? (
                                  val
                                ) : (
                                  <img
                                    src={cross}
                                    alt="not available"
                                    className="cross-icon"
                                  />
                                )}
                              </div>
                            )
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Proceed Button */}
      <div className="proceed-button-container">
        <button
          className="proceed-button"
          onClick={() => selectedTier && handleTier(selectedTier.tierName)}
        >
          <MoveRight
            size={16}
            color="#FFFFFF"
            style={{ marginRight: "6px" }}
          />
          Proceed
        </button>
      </div>
    </div>
  );
};

export default PricingModel;
